import {  createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { MixtureChooseEnum, MixtureState } from "../../entities/Mixture";
import {
  CondimentInterface,
  UpdateCondimentGramsPayload,
  UpdateCondimentPartsPayload,
  UpdateCondimentPercentagePayload,
} from "../../entities/Condiment";
import {
  getTotalGramsOfCondiments,
  getTotalPartsOfCondiments,
  getTotalPercentageOfCondiments,
} from "../../utils/configurator";
import { toFixedNumber } from "../../utils/toFixedNumber";
import { SettingsMix } from "../../entities/Settings";
import { getCalculatedRatioForCondiments } from "./actions";
import {
  CalculateRatioItemResponseInterface,
  CalculateRatioThunkResponse,
} from "../../entities/VolumeEngine";

const initialState: MixtureState = {
  name: "",
  nameHasError: false,
  condiments: [],
  totalParts: 0,
  totalGrams: 0,
  totalPercentage: 0,
  selectedWeight: 0,
  jars: 1,
  nrJarsLimitPerMix: 4,
  condimentLimitPerMix: 5,
  mixtureOption: MixtureChooseEnum.PARTS,
  mixtureIsLoading: false,
};

const mixtureSlice = createSlice({
  name: "mixtureSlice",
  initialState,
  reducers: {
    setSettingMix: (
      state: MixtureState,
      action: PayloadAction<SettingsMix>,
    ) => {
      state.nrJarsLimitPerMix = action.payload.nrJarsLimitPerMix;
      state.condimentLimitPerMix = action.payload.condimentLimitPerMix;
    },

    setInitialSetUp: (
      state: MixtureState,
      action: PayloadAction<CondimentInterface[]>,
    ) => {
      const initialStateOfCondiments = action.payload.map(
        (item: CondimentInterface) => ({
          ...item,
          meta: {
            ...item.meta,
            selected: false,
            parts: item.parts ?? 1,
          },
        }),
      );

      state.condiments = initialStateOfCondiments;
      state.totalParts = getTotalPartsOfCondiments(initialStateOfCondiments);
    },
    setSelectedWeight: (state: MixtureState, action: PayloadAction<number>) => {
      state.selectedWeight = action.payload;
    },
    setJars: (state: MixtureState, action: PayloadAction<number>) => {
      state.jars = action.payload;
    },
    updateCondimentParts: (
      state: MixtureState,
      action: PayloadAction<UpdateCondimentPartsPayload>,
    ) => {
      const condiments = [...state.condiments];

      const indexOfElement = condiments.findIndex(
        (item) => item.id === action.payload.condimentId,
      );
      const transformedElement = condiments[indexOfElement];
      transformedElement.meta.parts = action.payload.parts;
      condiments[indexOfElement] = transformedElement;

      const totalParts = getTotalPartsOfCondiments(condiments);

      const condimentsWithAdjustedPercentage = condiments.map((item) => ({
        ...item,
        meta: {
          ...item.meta,
        },
      }));

      state.condiments = condimentsWithAdjustedPercentage;
      state.totalParts = totalParts;
    },

    updateCondimentGrams: (
      state: MixtureState,
      action: PayloadAction<UpdateCondimentGramsPayload>,
    ) => {
      const condiments = [...state.condiments];

      const indexOfElement = condiments.findIndex(
        (item) => item.id === action.payload.condimentId,
      );
      const transformedElement = condiments[indexOfElement];
      transformedElement.meta.grams = action.payload.grams;
      condiments[indexOfElement] = transformedElement;

      const totalGrams = getTotalGramsOfCondiments(condiments);
      state.totalGrams = toFixedNumber(totalGrams, 1);
      state.selectedWeight = toFixedNumber(totalGrams, 1);
      state.condiments = condiments;
    },

    updateCondimentPercentage: (
      state: MixtureState,
      action: PayloadAction<UpdateCondimentPercentagePayload>,
    ) => {
      const condiments = [...state.condiments];

      const indexOfElement = condiments.findIndex(
        (item) => item.id === action.payload.condimentId,
      );

      const transformedElement = condiments[indexOfElement];
      transformedElement.meta.percentage = action.payload.percentage;
      condiments[indexOfElement] = transformedElement;

      const totalPercentage = getTotalPercentageOfCondiments(condiments);

      state.totalPercentage = toFixedNumber(totalPercentage, 1);
      state.condiments = condiments;
    },

    setNameHasError: (state: MixtureState, action: PayloadAction<boolean>) => {
      state.nameHasError = action.payload;
    },
    setMixtureName: (state: MixtureState, action: PayloadAction<string>) => {
      state.nameHasError = false;
      state.name = action.payload;
    },
    resetMixtureState: (state: MixtureState) => {
      state.condiments = initialState.condiments;
      state.selectedWeight = initialState.selectedWeight;
      state.jars = initialState.jars;
      state.totalParts = initialState.totalParts;
      state.name = initialState.name;
      state.mixtureOption = initialState.mixtureOption;
    },
    updateSelectedCondiments: (
      state: MixtureState,
      action: PayloadAction<CondimentInterface[]>,
    ) => {
      const condiments = [...state.condiments];

      const newCondiments = condiments.map((item) => {
        const newCondiment = action.payload.find(
          (condiment) => condiment.id === item.id,
        );

        if (newCondiment) {
          return {
            ...item,
            name: newCondiment.name,
          };
        }
        return item;
      });

      state.condiments = newCondiments;
    },

    setMixtureOption: (
      state: MixtureState,
      action: PayloadAction<MixtureChooseEnum>,
    ) => {
      state.mixtureOption = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCalculatedRatioForCondiments.pending, (state) => {
        state.mixtureIsLoading = true;
      })
      .addCase(getCalculatedRatioForCondiments.fulfilled, (
        state,
        action: PayloadAction<CalculateRatioThunkResponse>,
      ) => {
        const { condiments, nextMixtureOption } = action.payload;
        const condimentsWithCalculatedPropertiesMap = new Map(
          condiments.map((item: CalculateRatioItemResponseInterface) => [
            item.id,
            item,
          ]),
        );

        const condimentsWithAdjustedProperties = [...state.condiments].map(
          (item) => ({
            ...item,
            meta: {
              ...item.meta,
              parts:
                condimentsWithCalculatedPropertiesMap.get(item.id)?.parts ??
                item.meta.parts,
              grams:
                condimentsWithCalculatedPropertiesMap.get(item.id)?.weight ??
                item.meta.grams,
              percentage:
                condimentsWithCalculatedPropertiesMap.get(item.id)?.percentage ??
                item.meta.percentage,
            },
          }),
        );

        state.condiments = condimentsWithAdjustedProperties;
        state.mixtureIsLoading = false;
        state.mixtureOption = nextMixtureOption;
      })
      .addCase(getCalculatedRatioForCondiments.rejected, (state) => {
        state.mixtureIsLoading = false;
      });
  },
});

export const {
  setInitialSetUp,
  setSettingMix,
  setSelectedWeight,
  setJars,
  updateCondimentParts,
  updateCondimentGrams,
  updateCondimentPercentage,
  setMixtureName,
  setNameHasError,
  resetMixtureState,
  updateSelectedCondiments,
  setMixtureOption,
} = mixtureSlice.actions;

export default mixtureSlice.reducer;
